import React from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

export const formatDateRange = (startUtc, endUtc, timeZone = 'UTC') => {
    if (!startUtc || !endUtc) { return ''; }

    const toDate = (s) => new Date(s.endsWith('Z') ? s : `${s}Z`);
    const start = toDate(startUtc);
    const end = toDate(endUtc);

    const getParts = (date) => {
        const parts = new Intl.DateTimeFormat('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            timeZone,
        }).formatToParts(date);

        const map = Object.fromEntries(parts.map(p => [p.type, p.value]));
        return {
            month: map.month,
            day: Number(map.day),
            year: Number(map.year),
        };
    };

    const s = getParts(start);
    const e = getParts(end);

    // Single-day event
    if (s.day === e.day && s.month === e.month && s.year === e.year) {
        return `${s.month} ${s.day}, ${s.year}`;
    }

    // Same month/year
    if (s.month === e.month && s.year === e.year) {
        return `${s.month} ${s.day} - ${e.day}, ${s.year}`;
    }

    // Cross-month/year
    if (s.year === e.year) {
        return `${s.month} ${s.day} - ${e.month} ${e.day}, ${s.year}`;
    }

    return `${s.month} ${s.day}, ${s.year} - ${e.month} ${e.day}, ${e.year}`;
};

const RecurringEvents = ({ recurrentEvents = [], timeZone, noPadding = false }) => {
    const timeZones = useSelector(({ coreUI }) => coreUI.types.timeZones);
    // Normalize timezone to a valid IANA string
    let timeZoneValue = 'UTC';

    if (typeof timeZone === 'string' || typeof timeZone === 'number') {
        const tz = timeZones.find(tz => String(tz.id) === String(timeZone));
        timeZoneValue = tz?.value || tz?.name || 'UTC';
    } else if (typeof timeZone === 'object' && timeZone !== null) {
        timeZoneValue = timeZone.value || timeZone.name || 'UTC';
    }

    const [expanded, setExpanded] = React.useState(false);

    const visibleEvents = expanded ? recurrentEvents : recurrentEvents.slice(0, 5);

    const eventList = visibleEvents.map((event, i) => {
        const formattedDate = formatDateRange(event.startDate, event.endDate, timeZoneValue);
        return (
            <div className="pb8" key={event.tmsEventId || `event-${i}`}>
                <div className="icon-wrapper">
                    <div>
                        {formattedDate}
                    </div>
                </div>
                <div className="row">
                    <div className="col">
                        {event.tmsEventId ? (
                            <Link to={`/events/${event.tmsEventId}`} target="_blank" rel="noopener noreferrer">
                                {event.name}
                            </Link>
                        ) : (
                            <span>{event.name}</span>
                        )}
                    </div>
                </div>
            </div>
        );
    });

    if (noPadding) {
        return (
            <>
                {eventList}
            </>
        );
    }

    return (
        <div className="location-wrapper">
            <div className="location-details">
                {eventList}
                {recurrentEvents.length > 5 && (
                    <div className="mt16">
                        <div
                            className="clickable link text-bold"
                            onClick={() => setExpanded(prev => !prev)}
                        >
                            {expanded ? 'Show Less' : 'Show More'}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

RecurringEvents.propTypes = {
    recurrentEvents: PropTypes.arrayOf(PropTypes.object),
    timeZone: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.object]),
    noPadding: PropTypes.bool,
};

RecurringEvents.defaultProps = {
    recurrentEvents: [],
    timeZone: null,
    noPadding: false,
};

export default RecurringEvents;
